import { useCallback } from "react";
import { useSelector } from "react-redux";
import { selectCurrentTheater } from "modules/theater/selectors";
import { selectUserId } from "modules/auth/redux/selectors";
import {
  reportUserProblemFirebase,
  subscribeToUserProblemReports,
  unsubscribeToUserProblemReports,
} from "../userProblemReporter.firebase";
import { IUserProblem } from "../redux/types";
import { selectUserProblem } from "../redux/selectors";

const useUserProblemReporter = () => {
  const theater = useSelector(selectCurrentTheater);
  const userId = useSelector(selectUserId);
  const userProblems = useSelector(selectUserProblem);
  const theaterId = theater ? theater.id : null;

  const subscribe = useCallback(() => {
    if (theaterId) {
      subscribeToUserProblemReports(theaterId);
    }
  }, [theaterId]);

  const unsubscribe = useCallback(() => {
    if (theaterId && userId) {
      unsubscribeToUserProblemReports(theaterId, userId);
    }
  }, [theaterId, userId]);

  const resetMicCamProblem = useCallback(() => {
    if (theaterId && userId) {
      reportUserProblemFirebase(
        {
          OTError: false,
          noCam: false,
          noMic: false,
          notAllowed: false,
        },
        theaterId,
        userId,
      );
    }
  }, [theaterId, userId]);

  const reportProblem = useCallback(
    (data: IUserProblem) => {
      if (theaterId && userId) {
        reportUserProblemFirebase(data, theaterId, userId);
      }
    },
    [theaterId, userId],
  );

  const checkUserProblems = useCallback(
    (targetUserId: string) => {
      if (!userProblems[targetUserId]) {
        return false;
      }
      const { noCam, noMic, notAllowed } = userProblems[targetUserId];

      return noCam || noMic || notAllowed;
    },
    [userProblems],
  );

  return {
    subscribe,
    unsubscribe,
    reportProblem,
    resetMicCamProblem,
    checkUserProblems,
  };
};

export default useUserProblemReporter;
